import { getQueryParameterByName, replaceData, getAllQueryParameters, clone } from '../common';
import { setWindowTrygTealium, sendTrygTealium } from '../tracking';

/**
 * Get the initial discount. This is hardcoded in the json.
 * @param  {Object} packageData The json data.
 * @return {Object}             Object containing the discount.
 */
const getDiscountFromPackage = packageData => {
  const { initialValues: { discounts } } = packageData;
  let resultObj = {
    discount: 0,
    discountNoHouse: 0,
    linkNoHouse: null,
    trackingPackageName: '',
  };
  if (!discounts) {
    return null;
  }

  // Find a default price if one is defined.
  resultObj = discounts.reduce((result, discountObj) => {
    // If no products field is set it is the default.
    const {
      discount,
      products,
      discountNoHouse,
      linkNoHouse,
      trackingPackageName,
    } = discountObj;
    if (!products) {
      return {
        discount,
        discountNoHouse,
        linkNoHouse,
        trackingPackageName,
      };
    }
    return result;
  }, resultObj);

  const currentProducts = getQueryParameterByName('products');

  // Loop discounts to find one that matches.
  resultObj = discounts.reduce((result, discountObj) => {
    // If no products field is set it is the default.
    const {
      discount,
      products,
      discountNoHouse,
      linkNoHouse,
      trackingPackageName,
    } = discountObj;
    if (!products) {
      return result;
    }

    // Find the intersection of the 2 arrays. Both arrays must be equal length too.
    if (products.length !== currentProducts.length) {
      return result;
    }

    const intersection = products.filter(value => {
      return currentProducts.includes(value);
    });

    if (intersection.length !== currentProducts.length) {
      return result;
    }

    return {
      discount,
      discountNoHouse,
      linkNoHouse,
      trackingPackageName,
    };
  }, resultObj);

  return resultObj;
};

const getUserDataFromQueryParameter = packageData => {
  const { initialValues: { insertFromQueryParams } } = packageData;
  if (insertFromQueryParams) {
    const allQueryParams = getAllQueryParameters();
    return insertFromQueryParams.reduce((result, currentElement) => {
      if (Object.hasOwnProperty.call(allQueryParams, currentElement.key)) {
        let param = allQueryParams[currentElement.key];
        param = currentElement.type === 'boolean'
        || currentElement.type === 'number' ? JSON.parse(param) : param;
        return { ...result, [currentElement.key]: param };
      }
      return result;
    }, {});
  }

  return {};
};

/**
 * Get the basket list. This is hardcoded in the json.
 * @param  {Object} packageData The json data.
 * @return {Object}             Object containing the basket list.
 */
const getBasketListFromPackage = packageData => {
  const { initialValues: { basketList } } = packageData;

  const additionalProducts = getQueryParameterByName('products');
  const subProduct = getQueryParameterByName('subProduct');
  const quoteId = getQueryParameterByName('quoteId').length > 0 ? getQueryParameterByName('quoteId')[0] : null;

  const result = {
    basketList: [],
    price: '0',
    subProduct,
    quoteId,
  };

  if (basketList) {
    result.basketList = [
      ...additionalProducts,
      ...basketList,
    ];
  } else {
    result.basketList = [
      ...additionalProducts,
    ];
  }

  return result;
};

/**
 * Get the voucher. This is in the json.
 * @param  {Object} packageData The json data.
 * @return {Object}             Object containing the discount.
 */
const getDataFromPackage = (packageData) => {
  const { initialValues, initialValues: { insertToUserData } } = packageData;
  if (insertToUserData) {
    return insertToUserData.reduce((result, key) => {
      return initialValues[key] !== undefined ? { ...result, [key]: initialValues[key] } : result;
    }, {});
  }

  return {};
};

/**
 * Get the preSelectedUserData. This is in the json.
 * @param  {Object} packageData The json data.
 * @return {Object}             Object containing the Selected User Data.
 */
const getPreSelectedDataFromPackage = (packageData) => {
  const hasPreselectDefaults = Object.prototype.hasOwnProperty.call(packageData, 'preselectDefaults');
  if (hasPreselectDefaults) {
    const { preselectDefaults } = packageData;
    return preselectDefaults;
  }
  return {};
};

/**
 * Get the voucher. This is in the json.
 * @param  {Object} packageData The json data.
 * @return {Object}             Object containing the discount.
 */
const getVoucherFromPackage = (discount, packageData) => {
  const { initialValues: { vouchers } } = packageData;

  let resultObj = {
    hasVoucher: false,
    voucherAmount: 0,
    voucherDefId: null,
    voucherProductName: null,
  };

  if (!vouchers || vouchers.length === 0) {
    return resultObj;
  }

  resultObj = vouchers.reduce((result, voucher) => {
    const { eligible, voucherAmount, voucherDefId, productSubTypeCode, productName } = voucher;
    if (eligible) {
      if (!discount) {
        return {
          hasVoucher: true,
          voucherAmount,
          voucherDefId,
          voucherProductName: productName,
        };
      }
      // Match subType with the discount percentage.
      if (productSubTypeCode.indexOf(discount.discount) !== -1) {
        return {
          hasVoucher: true,
          voucherAmount,
          voucherDefId,
          voucherProductName: productName,
        };
      }
    }
    return result;
  }, resultObj);

  return resultObj;
};

/**
 * Get the initial discount. This is hardcoded in the json.
 * @param  {Object} packageData The json data.
 * @return {Object}             Object containing the discount.
 */
export const getInitialFromPackage = packageData => {
  let packageDataNew = packageData;
  let userDataNew = {};
  if (packageData.lastState && packageData.lastState != null) {
    packageDataNew = packageData.lastState.section.packageData;
    userDataNew = packageData.lastState.userData;
  } else {
    const discount = getDiscountFromPackage(packageData);
    const voucher = getVoucherFromPackage(discount, packageData);
    const basketList = getBasketListFromPackage(packageData);
    const queryParameters = getUserDataFromQueryParameter(packageData);
    const initialData = getDataFromPackage(packageData);
    const preSelectedUserData = getPreSelectedDataFromPackage(packageData);
    const allNodes = document.getElementsByTagName(import.meta.env.VITE_WRAPPER_TAG) || '{}';
    const receivedCustInfo = JSON.parse(allNodes[0].getAttribute('customerInfo'));
    const leadPartner = allNodes[0].getAttribute('leadpartner');
    const displayType = allNodes[0].getAttribute('format');
    const organizationCode = allNodes[0].getAttribute('organizationCode') || '';
    let basePath = import.meta.env.VITE_PUBLIC_URL;
    if (basePath) {
      // Somehow there is a bug where it chop the last character from the path,
      // so we insert it here.
      basePath += 'd';
    }

    // Adding postalCodeInput, if the postalCode is present.
    if(receivedCustInfo?.postalCode){
      receivedCustInfo.postalCodeInput = receivedCustInfo.postalCode;
    }

    const {
      initialValues: { trackingProductCategory, displayFormatList, tjmOrg },
    } = packageData;
    let preOrgDetails = {}
    if(organizationCode !== '' && tjmOrg.length > 0){
      const result = tjmOrg.find(({ key }) => key === organizationCode);
      const detailsdata = result.details;
      preOrgDetails.organizationCode = organizationCode;
      preOrgDetails.organizationCodeText = result.value;
      preOrgDetails.organizationCodeDetails = detailsdata;
      preOrgDetails.calculationType = detailsdata.calculationType;
      preOrgDetails.paymentLocationType = detailsdata.paymentLocationType;
      preOrgDetails.paymentType = detailsdata.paymentType;
      preOrgDetails.customerAgreement = detailsdata.discountCode;
      preOrgDetails.lonkontor = detailsdata.lonkontor;
      preOrgDetails.hasStudentDiscount = detailsdata.hasStudentDiscount;
      preOrgDetails.paymentFrequency = detailsdata.paymentFrequency;
    }
    
    userDataNew = {
      ...discount,
      ...voucher,
      ...basketList,
      ...queryParameters,
      ...initialData,
      ...preSelectedUserData,
      ...receivedCustInfo,
      ...preOrgDetails,
      basePath,
      trackingProductCategory,
    };
    userDataNew.displayFormat = (displayFormatList?.find(({ key }) => key === displayType))?.value || "" ;
    userDataNew.leadPartnerName = leadPartner;
  }
  setWindowTrygTealium(packageDataNew, userDataNew);
  return userDataNew;
};

/**
 * Gets parameters from the quote.
 * @param  {Object} quote         The quote received from the service.
 * @param  {Object} state         The state.
 * @param  {string} packageID     Optional PackageID.
 * @param  {boolean} getDefault   Get default property or not.
 * @return {Object} Object to spread into the state.
 */
export const getParametersFromQuote = (quote, state, packageID = null, getDefault = false) => {
  // Loop each product. Find the matching quote and add excess amount to the state.
  const { basketList, getFromQuote } = state;
  return basketList.reduce((result, item) => {
    const extra = {};
    if (getFromQuote && quote[getFromQuote]) {
      extra[getFromQuote] = quote[getFromQuote];
    }
    const properties = Object.keys(quote).filter(key => {
      return typeof quote[key] === 'string' ||
        quote[key] instanceof String ||
        (getDefault && key === 'coverageCodes');
    }).reduce((output, key) => {
      if (!packageID && getDefault) {
        // Use basketList item as prefix.
        const newItem = output[item] ? output[item] : {};
        newItem[key] = quote[key];
        return {
          ...output,
          [item]: newItem,
        };
      }
      const property = `${key}-${item}`;
      return {
        ...output,
        [getDefault ? key : property]: quote[key],
      };
    }, {});

    return {
      ...result,
      ...properties,
      ...extra,
    };
  }, {});
};

export const getUserDataField = (userData, field) => {
  if (field) {
    const newField = replaceData(field, userData, false, false, false);
    if (Object.hasOwnProperty.call(userData, newField)) {
      return userData[newField];
    }

    const keys = newField.split('.');

    return keys.reduce((result, key) => {
      if (result && Object.hasOwnProperty.call(result, key)) {
        return result[key];
      }

      return undefined;
    }, userData);
  }

  return undefined;
};

/**
 * Update User Data.
 * @param  {object} action  The action data to update.
 * @param  {object} state   The state to update.
 * @return {object}         The new updated state.
 */
export const changeUserData = (action, state) => {
  const { fields, formValuesOverride } = action;
  if (fields) {
    const userDataNew =
      formValuesOverride ?
        { ...state, ...action.formValues } :
        { ...action.formValues, ...state };
    fields.forEach(item => {
      if (Object.hasOwnProperty.call(userDataNew, item.field)) {
        if (item.condition) {
          // eslint-disable-next-line
          if (!eval(replaceData(item.condition, userDataNew))) {
            return;
          }
        }
        const value = replaceData(item.value, userDataNew, false, false, false);
        const id = replaceData(item.id, userDataNew, false, false, false);
        if (Array.isArray(userDataNew[item.field]) && !Array.isArray(value)) {
          const index = userDataNew[item.field].findIndex((e) => e.id === id);
          if (index === -1) {
            userDataNew[item.field].push({ id, value });
          } else {
            userDataNew[item.field][index].value = value;
          }
        } else {
          userDataNew[item.field] = clone(value);
        }
      } else {
        const keys = item.field.split('.');

        const userDataField = getUserDataField(userDataNew,
          keys.slice(0, keys.length - 1).join('.'));
        if (userDataField) {
          const valueFromUserData = replaceData(item.value, userDataNew, false, false, false);
          if (valueFromUserData !== undefined) {
            userDataField[keys[keys.length - 1]] = valueFromUserData;
          } else {
            userDataField[keys[keys.length - 1]] = item.value;
          }
        } else if (keys.length === 1) {
          const value = replaceData(item.value, userDataNew, false, false, false);
          userDataNew[item.field] = clone(value);
        }
      }

      if (action.formValues && typeof action.formValues.setFieldValue === 'function') {
        action.formValues.setFieldValue(item.field, userDataNew[item.field]);
      }
    });

    if (typeof action.setValues === 'function') {
      action.setValues(userDataNew);
    }
    return {
      ...userDataNew,
    };
  }

  return {
    ...state,
  };
};

export const updateCalculationJourney = state => {
  const newState = {
    ...state,
    calculationJourneyStarted: true,
  };
  sendTrygTealium('step1.1.1-start', newState, {});
  return newState;
};